import "./searchResults.scss";

import { useResizeObserver } from "@amzn/stencil-react-components/hooks";
import { Col, GridContainer, GridItem, Row, View } from "@amzn/stencil-react-components/layout";
import { Spinner, SpinnerSize } from "@amzn/stencil-react-components/spinner";
import { Text } from "@amzn/stencil-react-components/text";
import { NoResultsFound } from "common/components/NoResultsFound";
import { PageMainContainer } from "common/components/PageMainContainer/PageMainContainer";
import { CatalogItemSimplified, CatalogURLQueryStrings } from "common/types";
import {
  CatalogicalItem,
  CatalogItemSimplifiedWithRecommendations,
  convertToSimplified,
} from "common/types/catalogical-type";
import { getString } from "common/uistringlabels/uiStringUtils";
import { PERMISSIONS } from "config";
import { useSearchExperience } from "hooks/useSearchExperience";
import { UserPermissions } from "lib/services";
import {
  REC_SOURCE,
  Recommendation,
  RecommendationsService,
  transformTaxonomyIdForRecs,
} from "lib/services/recommendations-service";
import CatalogicalItemsList from "modules/components/CatalogicalItemsList";
import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import { HighlightProducts } from "./HighlightProducts";
import SearchBottomBar from "./SearchBottomBar";
import { SearchFilterPanel } from "./SearchFilterPanel";
import SearchSortingOptions from "./SearchSortingOptions";

const STRING_PATH = "searchResults.searchResult";
const EMPTY_STRING = "";
const CATALOG_ITEMS_LIST_CONTAINER_ID = "search-results-column";
const SEND_METRICS = true;

const SearchResultsV2: FC = () => {
  const toggleSearchExperience = UserPermissions.current.hasPermission(PERMISSIONS.TOGGLE_SEARCH_EXPERIENCE);
  const toggleHighlightProducts = UserPermissions.current.hasPermission(PERMISSIONS.TOGGLE_HIGHLIGHT_PRODUCTS);
  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
  const [loadingRecommendations, setLoadingRecommendations] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isSearchPending, setIsSearchPending] = useState(false);
  const location = useLocation();
  const { ref } = useResizeObserver<HTMLDivElement>();
  const observer = useRef<IntersectionObserver | null>(null);
  const lastElementRef = useRef<HTMLDivElement | null>(null);

  const urlParams = new URLSearchParams(location.search);
  const queryValue = urlParams.get(CatalogURLQueryStrings.QUERY)?.toLowerCase() ?? "";

  const {
    items,
    isLoading,
    isLoadingMore,
    hasMoreItems,
    searchCatalogItems,
    searchNext,
    totalCount,
    nextToken,
    actions,
    currentSort,
    nameSort,
    priceSort,
    bestMatchSort,
    filter,
    initializeFromUrl,
    shouldUseBestMatch,
  } = useSearchExperience<CatalogicalItem>();

  // Initialize filters and sorting from URL on component mount
  useEffect(() => {
    const params = new URLSearchParams(location.search);

    const sortParam = params.get(CatalogURLQueryStrings.SORT);
    const sortDirection = params.get(CatalogURLQueryStrings.SORT_DIRECTION);

    const categoryFilters = params.getAll(CatalogURLQueryStrings.CATEGORY);
    const osFilters = params.getAll(CatalogURLQueryStrings.OS);
    const costConditionFilters = params.getAll(CatalogURLQueryStrings.COST_CONDITION);

    initializeFromUrl({
      sort: sortParam && sortDirection ? `${sortParam},${sortDirection}` : undefined,
      filters: {
        category: categoryFilters.length > 0 ? categoryFilters : undefined,
        os: osFilters.length > 0 ? osFilters : undefined,
        costCondition: costConditionFilters.length > 0 ? costConditionFilters : undefined,
      },
    });

    // Mark as initialized after URL params are processed
    setIsInitialized(true);
  }, []);

  const updateUrl = useCallback(
    (newParams: Record<string, any>) => {
      const params = new URLSearchParams(location.search);

      const query = params.get(CatalogURLQueryStrings.QUERY);

      const newUrlParams = new URLSearchParams();
      if (query) {
        newUrlParams.set(CatalogURLQueryStrings.QUERY, query);
      }

      if (newParams.sortBy && newParams.sortDirection) {
        newUrlParams.set(CatalogURLQueryStrings.SORT, newParams.sortBy);
        newUrlParams.set(CatalogURLQueryStrings.SORT_DIRECTION, newParams.sortDirection);
      }

      if (newParams.filters) {
        Object.entries(newParams.filters).forEach(([key, values]) => {
          if (Array.isArray(values) && values.length > 0) {
            values.forEach((value) => {
              newUrlParams.append(key, value);
            });
          }
        });
      }

      const newUrl = `${location.pathname}?${newUrlParams.toString()}`;
      window.history.replaceState({}, "", newUrl);
    },
    [location.pathname, location.search]
  );

  const extractCategoryFromQuery = () => {
    const params = new URLSearchParams(location.search);
    const categoryFilters = params.getAll(CatalogURLQueryStrings.CATEGORY);
    return categoryFilters.length > 0 ? categoryFilters[0] : null;
  };

  // Fetch recommendations when component mounts or category changes
  useEffect(() => {
    const fetchRecommendations = async () => {
      setLoadingRecommendations(true);
      try {
        const recService = new RecommendationsService();
        const category = extractCategoryFromQuery();

        const response = await recService.getRecommendations({
          count: 15,
          ...(category && { category }),
        });
        setRecommendations(response.recommendations);
      } catch (error) {
        setRecommendations([]);
      } finally {
        setLoadingRecommendations(false);
      }
    };

    fetchRecommendations();
  }, [location.search]);

  const handleNameSortChange = useCallback(
    (value: any) => {
      actions.onNameSortChange(value);
      if (value) {
        const [sortBy, sortDirection] = value.split(",");
        updateUrl({
          sortBy,
          sortDirection,
          filters: filter,
        });
      } else {
        updateUrl({ filters: filter });
      }
    },
    [actions, filter, updateUrl]
  );

  const handlePriceSortChange = useCallback(
    (value: any) => {
      actions.onPriceSortChange(value);
      if (value) {
        const [sortBy, sortDirection] = value.split(",");
        updateUrl({
          sortBy,
          sortDirection,
          filters: filter,
        });
      } else {
        updateUrl({ filters: filter });
      }
    },
    [actions, filter, updateUrl]
  );

  const handleBestMatchSortChange = useCallback(
    (value: any) => {
      actions.onBestMatchSortChange(value);
      // When Best Match is selected, remove sort parameters from URL since it's the default
      // When Best Match is deselected, also remove sort parameters
      updateUrl({ filters: filter });
    },
    [actions, filter, updateUrl]
  );

  const handleFilterChange = useCallback(
    (newFilters: any) => {
      actions.onFilterChange(newFilters);
      updateUrl({
        sortBy: currentSort.sortBy,
        sortDirection: currentSort.sortDirection,
        filters: newFilters,
      });
    },
    [actions, currentSort, updateUrl]
  );

  const handleClearSorting = useCallback(() => {
    const hasQuery = queryValue.trim() !== "";

    if (hasQuery) {
      // Search mode: Reset to Best Match
      actions.onBestMatchSortChange("relevance");
      updateUrl({ filters: filter });
    } else {
      // Browse mode: Reset to Name A to Z
      actions.onNameSortChange("name,asc");
      updateUrl({
        sortBy: "name",
        sortDirection: "asc",
        filters: filter,
      });
    }
  }, [queryValue, actions, updateUrl, filter]);

  // Auto-apply default sorting based on context when query changes
  useEffect(() => {
    if (!isInitialized) return;

    const hasQuery = queryValue.trim() !== "";
    const hasActiveSort = !!(currentSort.sortBy && currentSort.sortDirection && currentSort.value !== "relevance");
    const hasAnySort = !!(nameSort.value || priceSort.value || bestMatchSort.value);

    if (hasQuery) {
      // Search mode: Only set Best Match if no sorting is currently applied or if wrong sorting is applied
      if (!bestMatchSort.value || hasActiveSort) {
        actions.onBestMatchSortChange("relevance");
        updateUrl({ filters: filter });
      }
    } else {
      // Browse mode: Only set Name A to Z if no sorting is currently applied or if wrong sorting is applied
      if (nameSort.value !== "name,asc" || bestMatchSort.value || priceSort.value) {
        actions.onNameSortChange("name,asc");
        updateUrl({
          sortBy: "name",
          sortDirection: "asc",
          filters: filter,
        });
      }
    }
  }, [
    isInitialized,
    queryValue, // This is the key dependency that triggers the reset
  ]);

  // Only trigger search after initialization is complete and sorting is stable
  useEffect(() => {
    // Skip if not initialized yet
    if (!isInitialized) {
      return;
    }

    // Set pending state to prevent "No Results" flash
    setIsSearchPending(true);

    // Add a small delay to allow sorting state to stabilize
    const timeoutId = setTimeout(() => {
      const request: any = {
        itemType: "software",
        searchQuery: queryValue,
        useSemanticSearch: true,
        simplifiedResults: true,
        caseInsensitive: true,
        limit: 50,
        filter: JSON.stringify({ ...actions.transformToCatalogicalFilters(filter) }),
      };

      // Only add sorting parameters if not using "Best Match" (relevance)
      if (currentSort.sortBy && currentSort.sortDirection && currentSort.value !== "relevance") {
        request.sortBy = currentSort.sortBy;
        request.sortDirection = currentSort.sortDirection;
      }

      searchCatalogItems(request, SEND_METRICS).finally(() => {
        setIsSearchPending(false);
      });
    }, 100); // Small delay to prevent race conditions

    return () => {
      clearTimeout(timeoutId);
      setIsSearchPending(false);
    };
  }, [queryValue, currentSort.sortBy, currentSort.sortDirection, currentSort.value, filter, isInitialized]);

  // Handle loading more items when scrolling
  /* istanbul ignore next */
  const onLoadMore = useCallback(() => {
    if (nextToken && !isLoading && !isLoadingMore) {
      const searchText = queryValue ?? EMPTY_STRING;
      const nextRequest: any = {
        itemType: "software",
        searchQuery: searchText,
        useSemanticSearch: true,
        simplifiedResults: true,
        limit: 50,
        // caseInsensitive: true,
        filter: JSON.stringify({ ...actions.transformToCatalogicalFilters(filter) }),
        offset: nextToken,
      };

      // Only add sorting parameters if not using "Best Match" (relevance)
      if (currentSort.sortBy && currentSort.sortDirection && currentSort.value !== "relevance") {
        nextRequest.sortBy = currentSort.sortBy;
        nextRequest.sortDirection = currentSort.sortDirection;
      }

      searchNext(nextRequest);
    }
  }, [nextToken, isLoading, queryValue, actions, filter, currentSort, searchNext]);

  // Setup intersection observer for infinite scrolling
  useEffect(() => {
    // Early return if loading, but keep the effect at the top level
    if (isLoading || !hasMoreItems) return;

    if (observer.current) observer.current.disconnect();

    /* istanbul ignore next */
    observer.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && nextToken) {
          onLoadMore();
        }
      },
      { threshold: 0.5 }
    );

    if (lastElementRef.current) {
      observer.current.observe(lastElementRef.current);
    }

    return () => {
      if (observer.current) observer.current.disconnect();
    };
  }, [isLoading, nextToken, hasMoreItems, onLoadMore]);

  /* istanbul ignore next */
  const shouldLoadMore = () => {
    if (!ref.current || !hasMoreItems) {
      return false;
    }
    // Check if we're near the bottom of the scroll container
    const scrollPosition = ref.current.scrollTop + ref.current.clientHeight;
    const scrollThreshold = ref.current.scrollHeight * 0.8; // Load more when within 20% of the bottom

    return scrollPosition >= scrollThreshold;
  };

  /* istanbul ignore next */
  const hasVisibleScroll = () => {
    if (ref?.current) {
      return ref.current.clientHeight < ref.current.scrollHeight;
    }

    return false;
  };

  const renderResultsText = () => {
    return (
      <Row dataTestId="search-results-text">
        <Text fontSize="T300" color="neutral90" fontWeight={600}>
          {queryValue?.trim()
            ? getString("catalogItemsList.resultsMessage", { totalCount: totalCount, text: queryValue })
            : getString("catalogItemsList.browseAllMessage", { totalCount: totalCount })}
        </Text>
      </Row>
    );
  };

  const renderCatalogResults = () => {
    const simplifiedItems = convertToSimplified(items);

    const itemsForDisplay: CatalogItemSimplifiedWithRecommendations[] = simplifiedItems.map((item) => {
      const normalizedItemTaxId = transformTaxonomyIdForRecs(item.itemId || EMPTY_STRING);

      const matchingRec = recommendations.find((rec: any) => rec.taxonomy_id === normalizedItemTaxId);

      return {
        ...item,
        recommendations:
          matchingRec?.rec_source === REC_SOURCE.TEAM
            ? getString(`${STRING_PATH}.usedByTeam`)
            : matchingRec?.rec_source === REC_SOURCE.ORG
            ? getString(`${STRING_PATH}.usedByOrg`)
            : matchingRec?.rec_source === REC_SOURCE.ROLE
            ? getString(`${STRING_PATH}.usedInRole`)
            : undefined,
      };
    });

    return (
      <CatalogicalItemsList
        onSearchNext={onLoadMore}
        scrollableTarget={CATALOG_ITEMS_LIST_CONTAINER_ID}
        shouldLoadMore={shouldLoadMore}
        useSearchExperience={toggleSearchExperience}
        isLoading={isLoading || loadingRecommendations}
        totalCount={totalCount}
        items={itemsForDisplay as CatalogItemSimplified[]}
      />
    );
  };

  function renderTopBar() {
    // Determine if Best Match should be shown - only when there's a search query
    const hasActiveSort = !!(currentSort.sortBy && currentSort.sortDirection && currentSort.value !== "relevance");
    const showBestMatch = queryValue.trim() !== "";
    // Best Match is active when there's a search query and no other sorting is applied
    const isBestMatchActive = queryValue.trim() !== "" && !hasActiveSort;

    return (
      <View dataTestId="search-top-bar" className="search-results-top" backgroundColor="white">
        <GridContainer isNested>
          <GridItem lg={12}>
            <Row width={"100%"}>{renderResultsText()}</Row>
            <Row width={"100%"}>
              <SearchSortingOptions
                priceSort={priceSort.value}
                alphabeticSort={nameSort.value}
                bestMatchSort={bestMatchSort.value}
                setPriceSort={handlePriceSortChange}
                setAlphabeticSort={handleNameSortChange}
                setBestMatchSort={handleBestMatchSortChange}
                searchQuery={queryValue}
                showBestMatch={showBestMatch}
                isBestMatchActive={isBestMatchActive}
                onClearSorting={handleClearSorting}
              />
            </Row>
          </GridItem>
        </GridContainer>
      </View>
    );
  }

  function renderHighlightProducts() {
    const categoriesForHighlight = filter.category || [];

    // HighlightProducts itself also guards, but doing it here avoids extra requests
    const shouldShowHighlight = queryValue.trim() === "" || categoriesForHighlight.length > 0;

    if (!shouldShowHighlight) {
      return null;
    }

    return <HighlightProducts isLoading={isLoading} categories={categoriesForHighlight} searchQuery={queryValue} />;
  }

  function getContent() {
    // Show loading state until initialization is complete or while search is pending
    if (!isInitialized || isLoading || isSearchPending) {
      return (
        <View padding="S700">
          <Spinner size={SpinnerSize.Medium} fr={undefined} />
        </View>
      );
    } else if (totalCount == 0) {
      // Show no results
      return (
        <>
          <Row padding={{ top: "S300" }} gridGap="S400" width={"100%"}>
            <Text fontSize="T300" className="search-results-number">
              {getString("noResults.searchResultText", { text: queryValue })}
            </Text>
          </Row>
          <NoResultsFound searchTerm={queryValue} />
        </>
      );
    } else {
      // Show results list
      return (
        <>
          {toggleHighlightProducts && renderHighlightProducts()}
          {renderTopBar()}
          {renderCatalogResults()}
          <div ref={lastElementRef} style={{ height: "20px", width: "100%" }}></div>
          {isLoadingMore && nextToken && (
            <Row justifyContent="center" padding={16}>
              <Spinner size={SpinnerSize.Small} />
            </Row>
          )}
          {!nextToken && items.length > 0 && (
            <Row justifyContent="center" padding={16}>
              <Text fontSize="T200" color="neutral70">
                {getString("catalogItemsList.noMoreItems", { defaultValue: "No more items to load" })}
              </Text>
            </Row>
          )}
        </>
      );
    }
  }

  return (
    <Row display="flex" justifyContent="space-between" dataTestId="search-results-v2" minHeight={"100vh"}>
      <Col display="flex" justifyContent="space-between" padding={{ top: "S500" }} width={"calc(100% - 304px)"}>
        <PageMainContainer>{getContent()}</PageMainContainer>
        <SearchBottomBar />
      </Col>
      <Col dataTestId="search-filter-panel" display="flex" width={"304px"}>
        {isInitialized && <SearchFilterPanel filters={filter} onApply={handleFilterChange} />}
      </Col>
    </Row>
  );
};

export default SearchResultsV2;
